import React, { useState } from 'react';
import Sidebar from '../../components/Sidebar';
import PaginationControls from '../../components/PaginationControls';
import Spinner from '../../components/Spinner';
import AuditModal from '../../components/AuditModal';
import useAuditLogs from '../../hooks/useAuditLogs';
import './AuditPage.css';

const AuditPage = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState({
    action: '',
    search: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({
    action: '',
    search: ''
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const { auditData, loading, error, refetch } = useAuditLogs(page, limit, appliedFilters);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleActionChange = (action) => {
    const newFilters = { ...filters, action };
    setFilters(newFilters);
    setAppliedFilters(newFilters);
    setPage(1);
  };

  const handleSearchClick = () => {
    setAppliedFilters(filters);
    setPage(1);
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      action: '',
      search: ''
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPage(1);
  };

    const handleRowClick = (log) => {
      setSelectedLog(log);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedLog(null);
  };

  const formatDate = (dateString) => {  
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  };

  const getActionLabel = (action) => {
    const labels = {
      'CREATE': 'Crear',
      'UPDATE': 'Actualizar',
      'DELETE': 'Eliminar'
    };
    return labels[action] || action;
  };

  if (error) {
    return (
      <div className="audit-page cms">
        <div className="main-content">
          <Sidebar />
          <div className="content">
            <div className="alert alert-danger" role="alert">
              Error al cargar los logs de auditoría: {error.message}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="audit-page cms">
      <div className="main-content">
        <Sidebar />
        <div className="content">
          <div className="d-flex align-items-center justify-content-between">
            <h2>Auditoría</h2>
          </div>
          <hr />
          
          {/* Filtros */}
          <div className="filters-container mb-4">
            <div className="row g-3">
              <div className="col-md-4">
                <select
                  className="form-select"
                  value={filters.action}
                  onChange={(e) => handleActionChange(e.target.value)}
                >
                  <option value="">Acción (Todas)</option>
                  <option value="CREATE">Crear</option>
                  <option value="UPDATE">Actualizar</option>
                  <option value="DELETE">Eliminar</option>
                </select>
              </div>
              <div className="col-md-4">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nombre..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearchClick();
                    }
                  }}
                />
              </div>
              <div className="col-md-4">
                <div className="d-flex gap-2">
                  <button 
                    className="btn btn-custom-primary" 
                    onClick={handleSearchClick}
                  >
                    Buscar
                  </button>
                  <button 
                    className="btn btn-outline-secondary" 
                    onClick={handleClearFilters}
                  >
                    Limpiar
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Loading */}
          {loading && <Spinner />}

          {/* Tabla */}
          {!loading && auditData && (
            <>
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead className="table-dark">
                    <tr>
                      <th>Fecha</th>
                      <th>Pizzeria</th>
                      <th>Acción</th>
                      <th>Usuario</th>
                      <th>Descripción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditData.data && auditData.data.map((log) => (
                      <tr 
                        key={log.id} 
                        onClick={() => handleRowClick(log)}
                        className="cursor-pointer"
                        title="Click para ver detalles"
                      >
                        <td>{formatDate(log.createdAt)}</td>
                        <td>{log.newValues.fantasy_name || 'N/A'}</td>
                        <td>
                          <span className={`badge bg-${log.action === 'CREATE' ? 'success' : 
                                           log.action === 'UPDATE' ? 'primary' : 'danger'}`}>
                            {getActionLabel(log.action)}
                          </span>
                        </td>
                        <td>{log.userEmail || 'N/A'}</td>
                        <td>{log.description || 'Sin descripción'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {auditData.data && auditData.data.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-muted">No se encontraron registros de auditoría</p>
                </div>
              )}              
              {/* Paginación */}
              {auditData.pagination.total > limit && (
                <PaginationControls
                  page={page}
                  total={auditData.pagination.total}
                  onPageChange={handlePageChange}
                  onLimitChange={handleLimitChange}
                  limit={limit}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal de Detalles */}
      {showModal && selectedLog && (
        <AuditModal
          log={selectedLog}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default AuditPage;
